import { NavigateFunction } from 'react-router-dom';

import { ConfigLinks } from '../config';
import { urler } from '../router/router-urler';
import { Skademeldingstype } from '../types/generated-types';
import { navigateToSitecorePage } from '../urler';
import { assertUnreachable } from '../utils/compiler-utils';

const {
    kjoretoyUrl,
    andreSkaderKjoretoyUrl,
    skadePaParkertKjoretoyUrl,
    kjoretoyTyveriUrl,
    kollisjonskadeKjoretoyUrl,
    batUrl,
    batKollisjonsskadeUrl,
    batTyveriEllerInnbruddUrl,
    degOgDineUforhetDodsfallEllerKritiskSykdomUrl,
    reiseUrl,
    reiseSykdomUlykkeDodsfallUrl,
    reiseMistetSkadetStjaletUrl,
    husOgHytteUrl,
    bygningBrannOgElektriskeSkaderUrl,
    bygningGlasskadeUrl,
    bygningNaturskadeUrl,
    innboOgEiendelerUrl,
    bygningLekkasjerOgVannskaderUrl,
    bygningInnbruddOgHarverkSkaderUrl,
    dyrUrl,
    hundUrl,
    hestUrl,
    kattUrl,
    rettshjelpUrl,
    rettshjelpPrivatUrl,
    ansvarPrivatUrl,
    ansvarLandbrukNaringUrl,
    landbrukUrl,
    landbrukProduksjonsdyrUrl,
    landbrukLosoreEiendelUrl,
    landbrukDriftsbygningerUrl,
    naringLosoreEiendelUrl,
} = urler();

interface UrlInfo {
    url: string | undefined;
    navigateToSitecorePage: boolean;
}

const getBackUrl = (skademeldingstype: Skademeldingstype, links: ConfigLinks): UrlInfo => {
    switch (skademeldingstype) {
        case Skademeldingstype.InnboEiendelerSykkeltyveri:
        case Skademeldingstype.InnboEiendelerTyveri:
            return { url: innboOgEiendelerUrl, navigateToSitecorePage: false };

        case Skademeldingstype.MotorOgAndreMaskinSkader:
        case Skademeldingstype.Glasskade:
        case Skademeldingstype.Veihjelp:
        case Skademeldingstype.KollisjonMedAnnetKjoretoy:
            return { url: kjoretoyUrl, navigateToSitecorePage: false };

        case Skademeldingstype.KollisjonBulkingEllerHarvarkPaParkertKjoretoy:
        case Skademeldingstype.TakrasPaParkertKjoretoy:
            return { url: skadePaParkertKjoretoyUrl, navigateToSitecorePage: false };

        case Skademeldingstype.VarrelaterteSkader:
        case Skademeldingstype.Feilfylling:
        case Skademeldingstype.BrannskadePaKjoretoy:
        case Skademeldingstype.MistetNokkel:
        case Skademeldingstype.TyveriAvNokkel:
            return { url: andreSkaderKjoretoyUrl, navigateToSitecorePage: false };

        case Skademeldingstype.KjoretoyTyveriInnbrudd:
        case Skademeldingstype.KjoretoyTyveriStjaletOgFunnet:
        case Skademeldingstype.KjoretoyTyveriStjaletOgIkkeFunnet:
            return { url: kjoretoyTyveriUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BatGrunnstottEllerSkjar:
        case Skademeldingstype.BatHarKollidertMedAnnenBat:
        case Skademeldingstype.BatHarTruffetFlytendeGjenstand:
        case Skademeldingstype.BatHarKollidertMedBryggeEllerFastland:
            return { url: batKollisjonsskadeUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BatInnbrudd:
        case Skademeldingstype.BatStjaletOgFunnet:
        case Skademeldingstype.BatStjaletOgIkkeFunnet:
            return { url: batTyveriEllerInnbruddUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BatHarSunket:
        case Skademeldingstype.BatHarSlittSegFraFortoyning:
        case Skademeldingstype.BatMaskinskader:
            return { url: batUrl, navigateToSitecorePage: false };

        case Skademeldingstype.KollisjonskadePaKjoretoyMykTrafikant:
        case Skademeldingstype.KollisjonskadePaKjoretoySteinIsEllerAnnetObjekt:
        case Skademeldingstype.KollisjonskadePaKjoretoyDyr:
        case Skademeldingstype.UtforkjoringEllerKollisjonMedHusMurkant:
        case Skademeldingstype.UtforkjoringEllerKollisjonMedAutovernEllerSkilt:
            return { url: kollisjonskadeKjoretoyUrl, navigateToSitecorePage: false };

        case Skademeldingstype.PersonskadeUlykke:
        case Skademeldingstype.PersonskadeBarneOgUngdomsforsikring:
        case Skademeldingstype.PersonskadeAnnet:
            return { url: links.degogdinelandingsside.url, navigateToSitecorePage: true };

        case Skademeldingstype.PersonskadeArbeidsuforhet:
        case Skademeldingstype.PersonskadeKritiskSykdom:
        case Skademeldingstype.PersonskadeDodsfall:
            return { url: degOgDineUforhetDodsfallEllerKritiskSykdomUrl, navigateToSitecorePage: false };

        case Skademeldingstype.Reiseavbestilling:
        case Skademeldingstype.ForsinketBagasje:
        case Skademeldingstype.ForsinketTransport:
            return { url: reiseUrl, navigateToSitecorePage: false };

        case Skademeldingstype.MistetReisegods:
        case Skademeldingstype.SkadetReisegods:
        case Skademeldingstype.ReisegodsTyveri:
        case Skademeldingstype.GjenglemtReisegods:
            return { url: reiseMistetSkadetStjaletUrl, navigateToSitecorePage: false };

        case Skademeldingstype.ReiseSykdomSkadeEllerUlykkePaReisen:
        case Skademeldingstype.ReiseDodsfallEllerAlvorligSykdomPaReisen:
            return { url: reiseSykdomUlykkeDodsfallUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningNaturskadeFlom:
        case Skademeldingstype.BygningNaturskadeStorm:
        case Skademeldingstype.BygningNaturskadeSkred:
        case Skademeldingstype.BygningNaturskadeAnnen:
            return { url: bygningNaturskadeUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningAnnet:
            return { url: husOgHytteUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningVannskadeLekkasjeInne:
        case Skademeldingstype.BygningVannskadeLekkasjeUtvendigRor:
        case Skademeldingstype.BygningVannskadeTilbakeslag:
        case Skademeldingstype.BygningVannskadeHustakYttervegg:
        case Skademeldingstype.BygningVannskadeKjeller:
        case Skademeldingstype.BygningVannskadeAnnet:
            return { url: bygningLekkasjerOgVannskaderUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningElektriskSkade:
        case Skademeldingstype.BygningBrannskadeEllerSot:
        case Skademeldingstype.BygningBrannskadeAnnen:
            return { url: bygningBrannOgElektriskeSkaderUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningGlasskadeAnnen:
        case Skademeldingstype.BygningGlasskadeIPlatetoppKomfyr:
        case Skademeldingstype.BygningGlasskadeVinduDor:
        case Skademeldingstype.BygningGlasskadeSanitarporselenDusjvegg:
            return { url: bygningGlasskadeUrl, navigateToSitecorePage: false };

        case Skademeldingstype.BygningInnbrudd:
        case Skademeldingstype.BygningHarverk:
            return { url: bygningInnbruddOgHarverkSkaderUrl, navigateToSitecorePage: false };

        case Skademeldingstype.InnboEiendelerTvElektriskeArtikler:
        case Skademeldingstype.InnboEiendelerMatvarerFryserKjoleskap:
        case Skademeldingstype.InnboEiendelerMobilNettbrettPc:
        case Skademeldingstype.InnboEiendelerSkadePaAndreEiendeler:
            return { url: innboOgEiendelerUrl, navigateToSitecorePage: false };

        case Skademeldingstype.Hund:
            return { url: hundUrl, navigateToSitecorePage: false };
        case Skademeldingstype.Katt:
            return { url: kattUrl, navigateToSitecorePage: false };
        case Skademeldingstype.Hest:
            return { url: hestUrl, navigateToSitecorePage: false };
        case Skademeldingstype.Dyr:
            return { url: dyrUrl, navigateToSitecorePage: false };

        case Skademeldingstype.Rettshjelp:
            return { url: rettshjelpUrl, navigateToSitecorePage: false };

        case Skademeldingstype.RettshjelpTvistKjoretoy:
        case Skademeldingstype.RettshjelpTvistBolig:
        case Skademeldingstype.RettshjelpTvistEntreprenorEllerHandverker:
        case Skademeldingstype.RettshjelpTvistNabo:
        case Skademeldingstype.RettshjelpTvistJordskifteEllerFesterett:
        case Skademeldingstype.RettshjelpTvistOffentligForvaltningsvedtak:
        case Skademeldingstype.RettshjelpTvistAnnenType:
            return { url: rettshjelpPrivatUrl, navigateToSitecorePage: false };

        case Skademeldingstype.AnsvarPrivatPersonskade:
        case Skademeldingstype.AnsvarPrivatTingskade:
            return { url: ansvarPrivatUrl, navigateToSitecorePage: false };

        case Skademeldingstype.AnsvarLandbrukNaringFormueskade:
        case Skademeldingstype.AnsvarLandbrukNaringTingskade:
            return { url: ansvarLandbrukNaringUrl, navigateToSitecorePage: false };

        case Skademeldingstype.SmaElektriskeKjoretoy:
            return { url: links.meldskadelandingsside.url, navigateToSitecorePage: true };

        case Skademeldingstype.LandbrukDriftsbygninger:
            return { url: landbrukDriftsbygningerUrl, navigateToSitecorePage: false };
        case Skademeldingstype.LandbrukLosore:
            return { url: landbrukLosoreEiendelUrl, navigateToSitecorePage: false };
        case Skademeldingstype.LandbrukAvling:
        case Skademeldingstype.LandbrukBrannvarslingsanlegg:
        case Skademeldingstype.LandbrukMelkerobot:
            return { url: landbrukUrl, navigateToSitecorePage: false };

        case Skademeldingstype.LandbrukProduksjonsdyrStorfe:
        case Skademeldingstype.LandbrukProduksjonsdyrIndividuellOkse:
            return { url: landbrukProduksjonsdyrUrl, navigateToSitecorePage: false };

        case Skademeldingstype.Ansvar:
        case Skademeldingstype.Naring:
        case Skademeldingstype.Landbruk:
        case Skademeldingstype.LandbrukProduksjonsdyr:
        case Skademeldingstype.Annet:
            return { url: undefined, navigateToSitecorePage: false };

        case Skademeldingstype.NaringTyveriUnderslag:
            return { url: naringLosoreEiendelUrl, navigateToSitecorePage: false };
        case Skademeldingstype.NaringBygningsglass:
            return { url: naringLosoreEiendelUrl, navigateToSitecorePage: false };

        default:
            return assertUnreachable(skademeldingstype);
    }
};

export const redirectToLandingsside = (
    skadetype: Skademeldingstype,
    links: ConfigLinks,
    navigate: NavigateFunction,
    goBack: () => void,
) => {
    const urlInfo: UrlInfo = getBackUrl(skadetype, links);
    if (urlInfo.navigateToSitecorePage && urlInfo.url !== undefined) navigateToSitecorePage(urlInfo.url);
    else if (!urlInfo.navigateToSitecorePage && urlInfo.url !== undefined) navigate(urlInfo.url);
    else goBack();
};
